import { Platform, DeviceEventEmitter, EmitterSubscription } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

const ICON_PREFERENCE_KEY = 'app_icon_preference';

export type AppIconType = 'default' | 'Plus';

class AppIconService {
  private isSupported: boolean | null = null;
  private currentIcon: AppIconType = 'default';
  private module: any = null;
  private subscriptionListener: EmitterSubscription | null = null;

  private isProductionBuild(): boolean {
    return !__DEV__;
  }

  private getModule(): any {
    if (!this.isProductionBuild()) {
      return null;
    }

    if (this.module === undefined) {
      return null;
    }
    
    if (this.module === null) {
      try {
        this.module = require('expo-alternate-app-icons');
      } catch (e) {
        console.log('📱 expo-alternate-app-icons not available');
        this.module = undefined;
        return null;
      }
    }
    
    return this.module;
  }

  private checkSupport(): boolean {
    if (!this.isProductionBuild()) {
      return false;
    }

    if (this.isSupported !== null) {
      return this.isSupported;
    }

    try {
      if (Platform.OS !== 'ios') {
        this.isSupported = false;
        return false;
      }

      const mod = this.getModule();
      if (!mod) {
        this.isSupported = false;
        return false;
      }

      this.isSupported = mod.supportsAlternateIcons === true;
      return this.isSupported;
    } catch (e) {
      console.log('📱 Error checking alternate icons support:', e);
      this.isSupported = false;
      return false;
    }
  }

  async initialize(): Promise<void> {
    if (!this.isProductionBuild()) {
      console.log('📱 Dynamic app icons disabled in development mode');
      return;
    }

    if (!this.checkSupport()) {
      console.log('📱 Alternate app icons not supported on this platform');
      return;
    }

    try {
      const mod = this.getModule();
      if (mod && typeof mod.getAppIconName === 'function') {
        const iconName = mod.getAppIconName();
        this.currentIcon = iconName === 'Plus' ? 'Plus' : 'default';
        console.log(`📱 Current app icon: ${this.currentIcon}`);
      }
    } catch (error) {
      console.log('📱 Error initializing app icon service:', error);
    }

    // Subscribe to the GLOBAL RevenueCat customer-info update event so the
    // icon stays in sync regardless of which screen is mounted when the
    // user upgrades / downgrades / restores. Previously the only consumer
    // was usePadrinhoPlus(), which is mounted only on the meeting detail
    // screen — so a Plus purchase from any other paywall (trail audio,
    // sponsor, subscription, etc.) never updated the icon until the user
    // happened to open a meeting page. This listener closes that gap.
    if (this.subscriptionListener) {
      this.subscriptionListener.remove();
      this.subscriptionListener = null;
    }
    this.subscriptionListener = DeviceEventEmitter.addListener(
      'revenuecat:customer-info-updated',
      (data: { hasPadrinhoPlus?: boolean }) => {
        if (typeof data?.hasPadrinhoPlus !== 'boolean') return;
        console.log('📱 [appIconService] customer-info-updated → syncing icon', {
          hasPadrinhoPlus: data.hasPadrinhoPlus,
        });
        this.setIconForSubscription(data.hasPadrinhoPlus).catch((e) =>
          console.log('📱 Icon sync from event failed (non-fatal):', e)
        );
      }
    );
  }

  isAlternateIconsSupported(): boolean {
    return this.checkSupport();
  }

  getCurrentIcon(): AppIconType {
    return this.currentIcon;
  }

  async setIconForSubscription(hasPadrinhoPlus: boolean): Promise<void> {
    if (!this.checkSupport()) {
      console.log('📱 Alternate icons not supported, skipping icon change');
      return;
    }

    const targetIcon: AppIconType = hasPadrinhoPlus ? 'Plus' : 'default';
    
    if (this.currentIcon === targetIcon) {
      console.log(`📱 App icon already set to ${targetIcon}, no change needed`);
      return;
    }

    const mod = this.getModule();
    if (!mod) {
      return;
    }

    try {
      if (hasPadrinhoPlus) {
        console.log('📱 Setting Plus app icon for subscriber...');
        if (typeof mod.setAlternateAppIcon === 'function') {
          await mod.setAlternateAppIcon('Plus');
          this.currentIcon = 'Plus';
          await AsyncStorage.setItem(ICON_PREFERENCE_KEY, 'Plus');
          console.log('✅ Successfully set Plus app icon');
        }
      } else {
        console.log('📱 Resetting to default app icon...');
        if (typeof mod.resetAppIcon === 'function') {
          await mod.resetAppIcon();
          this.currentIcon = 'default';
          await AsyncStorage.setItem(ICON_PREFERENCE_KEY, 'default');
          console.log('✅ Successfully reset to default app icon');
        }
      }
    } catch (error) {
      console.log('📱 Error changing app icon:', error);
    }
  }

  async setPlusIcon(): Promise<boolean> {
    if (!this.checkSupport()) {
      return false;
    }

    const mod = this.getModule();
    if (!mod || typeof mod.setAlternateAppIcon !== 'function') {
      return false;
    }

    try {
      await mod.setAlternateAppIcon('Plus');
      this.currentIcon = 'Plus';
      await AsyncStorage.setItem(ICON_PREFERENCE_KEY, 'Plus');
      console.log('✅ Set Plus app icon');
      return true;
    } catch (error) {
      console.log('📱 Error setting Plus icon:', error);
      return false;
    }
  }

  async setDefaultIcon(): Promise<boolean> {
    if (!this.checkSupport()) {
      return false;
    }

    const mod = this.getModule();
    if (!mod || typeof mod.resetAppIcon !== 'function') {
      return false;
    }

    try {
      await mod.resetAppIcon();
      this.currentIcon = 'default';
      await AsyncStorage.setItem(ICON_PREFERENCE_KEY, 'default');
      console.log('✅ Reset to default app icon');
      return true;
    } catch (error) {
      console.log('📱 Error resetting to default icon:', error);
      return false;
    }
  }

  async getSavedIconPreference(): Promise<AppIconType> {
    try {
      const preference = await AsyncStorage.getItem(ICON_PREFERENCE_KEY);
      return preference === 'Plus' ? 'Plus' : 'default';
    } catch (error) {
      console.log('📱 Error getting icon preference:', error);
      return 'default';
    }
  }
}

export const appIconService = new AppIconService();
export default appIconService;
